/**
 * CRM-wide presence hours: classify idle-aware segments into in-hours vs
 * after-hours using each user's IANA timezone. Planning signal only.
 */
import { getUtcOffsetMinutes, isValidIanaTimezone } from './userTimezone';
import { isCrmStaffUser } from './userRoles';
import type { CRMUser, UserRole } from '../types';

export const CRM_HOURS_FALLBACK_TZ = 'America/Chicago';
export const CRM_BUSINESS_START_HOUR = 8;
export const CRM_BUSINESS_END_HOUR = 18;

export type CrmHoursPeriod = 'week' | 'month' | 'year' | 'all';

export interface CrmActivitySlice {
  dateKey: string;
  monthKey: string;
  minutesInHours: number;
  minutesAfterHours: number;
}

export interface CrmHoursPeriodRange {
  startDateKey: string;
  endDateKey: string;
  startMonthKey: string;
  endMonthKey: string;
}

export interface CrmHoursDoc {
  userId: string;
  minutesInHours: number;
  minutesAfterHours: number;
  timezone?: string;
}

export interface CrmHoursRow {
  userId: string;
  displayName: string;
  timezone: string;
  timezoneFallback: boolean;
  minutesInHours: number;
  minutesAfterHours: number;
  extraPct: number | null;
}

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

export function resolveCrmActivityTimezone(tz?: string | null): string {
  if (tz && isValidIanaTimezone(tz)) return tz.trim();
  return CRM_HOURS_FALLBACK_TZ;
}

export function isCrmHoursTimezoneFallback(tz?: string | null): boolean {
  return !tz || !isValidIanaTimezone(tz);
}

interface LocalParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  weekday: number;
}

export function localPartsInZone(date: Date, timeZone: string): LocalParts {
  const tz = resolveCrmActivityTimezone(timeZone);
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const parts = dtf.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  const weekdayName = get('weekday');
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour: Number(get('hour')),
    minute: Number(get('minute')),
    weekday: WEEKDAY_INDEX[weekdayName] ?? 0,
  };
}

/** Convert a civil time in `timeZone` to a UTC Date. */
export function utcFromLocalParts(
  timeZone: string,
  year: number,
  month: number,
  day: number,
  hour: number,
  minute = 0,
): Date {
  const tz = resolveCrmActivityTimezone(timeZone);
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, 0);
  const offsetMin = getUtcOffsetMinutes(tz, new Date(utcGuess));
  let result = new Date(utcGuess - offsetMin * 60_000);
  const offset2 = getUtcOffsetMinutes(tz, result);
  if (offset2 !== offsetMin) {
    result = new Date(utcGuess - offset2 * 60_000);
  }
  return result;
}

export function dateKeyInZone(date: Date, timeZone: string): string {
  const p = localPartsInZone(date, timeZone);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

export function monthKeyInZone(date: Date, timeZone: string): string {
  const p = localPartsInZone(date, timeZone);
  return `${p.year}-${String(p.month).padStart(2, '0')}`;
}

export function monthKeyFromDateKey(dateKey: string): string {
  return dateKey.slice(0, 7);
}

export function isInBusinessHours(date: Date, timeZone: string): boolean {
  const p = localPartsInZone(date, timeZone);
  if (p.weekday === 0 || p.weekday === 6) return false;
  return p.hour >= CRM_BUSINESS_START_HOUR && p.hour < CRM_BUSINESS_END_HOUR;
}

function addDaysToDateKey(dateKey: string, days: number): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  const utc = new Date(Date.UTC(y, m - 1, d + days));
  return `${utc.getUTCFullYear()}-${String(utc.getUTCMonth() + 1).padStart(2, '0')}-${String(utc.getUTCDate()).padStart(2, '0')}`;
}

function weekdayOfDateKey(dateKey: string, timeZone: string): number {
  const [y, m, d] = dateKey.split('-').map(Number);
  return localPartsInZone(utcFromLocalParts(timeZone, y, m, d, 12, 0), timeZone).weekday;
}

/**
 * Split [start, end) into per-local-day in-hours / after-hours minutes.
 * Walks one local minute at a time so 8am / 6pm / weekend edges stay accurate.
 */
export function classifyActivitySegment(
  start: Date,
  end: Date,
  timeZone: string,
): CrmActivitySlice[] {
  const tz = resolveCrmActivityTimezone(timeZone);
  const startMs = start.getTime();
  const endMs = end.getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return [];

  const byDate = new Map<string, CrmActivitySlice>();
  let cursor = startMs;
  while (cursor < endMs) {
    const instant = new Date(cursor);
    const parts = localPartsInZone(instant, tz);
    const nextHour = utcFromLocalParts(
      tz,
      parts.year,
      parts.month,
      parts.day,
      parts.hour,
      parts.minute + 1,
    );
    const chunkEnd = Math.min(endMs, nextHour.getTime());
    if (chunkEnd <= cursor) {
      cursor += 1000;
      continue;
    }
    const minutes = (chunkEnd - cursor) / 60_000;
    const dateKey = dateKeyInZone(instant, tz);
    let row = byDate.get(dateKey);
    if (!row) {
      row = {
        dateKey,
        monthKey: monthKeyFromDateKey(dateKey),
        minutesInHours: 0,
        minutesAfterHours: 0,
      };
      byDate.set(dateKey, row);
    }
    if (isInBusinessHours(instant, tz)) row.minutesInHours += minutes;
    else row.minutesAfterHours += minutes;
    cursor = chunkEnd;
  }

  return Array.from(byDate.values()).filter(
    (s) => s.minutesInHours > 0 || s.minutesAfterHours > 0,
  );
}

export function crmHoursPeriodRange(
  period: Exclude<CrmHoursPeriod, 'all'>,
  now: Date = new Date(),
  timeZone: string = CRM_HOURS_FALLBACK_TZ,
): CrmHoursPeriodRange {
  const tz = resolveCrmActivityTimezone(timeZone);
  const today = dateKeyInZone(now, tz);
  const parts = localPartsInZone(now, tz);

  if (period === 'week') {
    const wd = weekdayOfDateKey(today, tz);
    const mondayOffset = wd === 0 ? -6 : 1 - wd;
    const startDateKey = addDaysToDateKey(today, mondayOffset);
    return {
      startDateKey,
      endDateKey: today,
      startMonthKey: monthKeyFromDateKey(startDateKey),
      endMonthKey: monthKeyFromDateKey(today),
    };
  }

  if (period === 'month') {
    const startDateKey = `${parts.year}-${String(parts.month).padStart(2, '0')}-01`;
    return {
      startDateKey,
      endDateKey: today,
      startMonthKey: monthKeyFromDateKey(startDateKey),
      endMonthKey: monthKeyFromDateKey(today),
    };
  }

  const startDateKey = `${parts.year}-01-01`;
  return {
    startDateKey,
    endDateKey: today,
    startMonthKey: `${parts.year}-01`,
    endMonthKey: `${parts.year}-12`,
  };
}

export function aggregateCrmHoursRows(
  docs: CrmHoursDoc[],
  users: Array<Pick<CRMUser, 'uid' | 'displayName' | 'timezone' | 'role'>>,
): CrmHoursRow[] {
  const userById = new Map(users.map((u) => [u.uid, u]));
  const totals = new Map<string, { inHours: number; afterHours: number }>();
  for (const doc of docs) {
    if (!doc.userId) continue;
    const role = userById.get(doc.userId)?.role as UserRole | undefined;
    if (role && !isCrmStaffUser(role)) continue;
    const cur = totals.get(doc.userId) ?? { inHours: 0, afterHours: 0 };
    cur.inHours += doc.minutesInHours || 0;
    cur.afterHours += doc.minutesAfterHours || 0;
    totals.set(doc.userId, cur);
  }

  const rows: CrmHoursRow[] = [];
  Array.from(totals.entries()).forEach(([userId, tot]) => {
    const user = userById.get(userId);
    const rawTz = user?.timezone;
    rows.push({
      userId,
      displayName: user?.displayName || 'Unknown user',
      timezone: resolveCrmActivityTimezone(rawTz),
      timezoneFallback: isCrmHoursTimezoneFallback(rawTz),
      minutesInHours: tot.inHours,
      minutesAfterHours: tot.afterHours,
      extraPct: tot.inHours + tot.afterHours > 0
        ? tot.afterHours / (tot.inHours + tot.afterHours)
        : null,
    });
  });

  return rows.sort((a, b) => {
    if (b.minutesAfterHours !== a.minutesAfterHours) {
      return b.minutesAfterHours - a.minutesAfterHours;
    }
    return a.displayName.localeCompare(b.displayName);
  });
}

export function formatCrmMinutes(minutes: number): string {
  const rounded = Math.round(minutes);
  if (rounded <= 0) return '0m';
  const h = Math.floor(rounded / 60);
  const m = rounded % 60;
  if (h <= 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export function formatCrmExtraPct(pct: number | null): string {
  if (pct == null || !Number.isFinite(pct)) return '—';
  return `${Math.round(pct * 100)}%`;
}

export function dailyDocId(userId: string, dateKey: string): string {
  return `${userId}_${dateKey}`;
}

export function monthlyDocId(userId: string, monthKey: string): string {
  return `${userId}_${monthKey}`;
}
